import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useShopProducts } from '@/hooks/useShop';
import { formatPrice } from '@/lib/pricing';
import { useShopEnabled } from '@/hooks/usePlatformSettings';

export default function ShopTeaser() {
  const { shopEnabled } = useShopEnabled();
  const { data: products = [] } = useShopProducts({ featured: true });
  const display = products.slice(0, 4);

  if (!shopEnabled || !display.length) return null;

  return (
    <section className="relative border-t border-white/10 py-24 md:py-28 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase mb-4" style={{ color: '#f8ff3a' }}>
              // BOUTIQUE OFFICIELLE
            </div>
            <h2 className="m-0 leading-[0.95]">
              <span className="font-display italic uppercase -skew-x-6 inline-block text-[40px] md:text-[56px] lg:text-[72px] text-foreground">
                ÉQUIPE-TOI{' '}
              </span>
              <span
                className="font-display italic uppercase -skew-x-6 inline-block text-[40px] md:text-[56px] lg:text-[72px] text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(90deg, #f8ff3a 0%, #caff00 50%, #00e5ff 100%)' }}
              >
                KOURSE.
              </span>
            </h2>
            <p className="mt-4 max-w-[520px] text-[15px] leading-relaxed text-muted-foreground">
              Textile premium, accessoires et drops limités pour les coureurs qui veulent porter la marque.
            </p>
          </div>
          <Button variant="outline" asChild className="self-start lg:self-end border-white/20 hover:bg-white/5">
            <Link to="/boutique">
              Voir la boutique
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {display.map((product) => (
            <Link
              key={product.id}
              to={`/boutique/${product.slug}`}
              className="group block"
            >
              <div className="relative aspect-[4/5] rounded-xl border border-white/10 bg-card overflow-hidden mb-3">
                {product.primary_image_url ? (
                  <img
                    src={product.primary_image_url}
                    alt={product.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-[#1a1a22] to-[#0a0a0c] transition-transform duration-500 group-hover:scale-105">
                    <span className="font-display italic uppercase text-2xl -skew-x-6 text-white/20">Kourse</span>
                  </div>
                )}
                {product.badge && (
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-[0.08em]">
                    {product.badge}
                  </span>
                )}
                {product.is_preorder && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-[0.08em]">
                    <Clock className="h-3 w-3" /> Préco
                  </span>
                )}
              </div>
              <div className="space-y-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {product.category}
                </p>
                <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                  {product.name}
                </p>
                <p className="font-display italic text-lg -skew-x-6 text-primary">
                  {formatPrice(product.base_price_cents)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
